import { useState, useEffect, useCallback } from "react";
import { Layout } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import {
  ShoppingCart, Package, Truck, DollarSign, Clock, CheckCircle2,
  XCircle, AlertTriangle, MapPin, Wrench, TrendingUp, Eye,
  ChevronDown, ChevronUp, Phone, Mail, Search, RefreshCw, MessageCircle, CalendarCheck, Trash2, ExternalLink
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const formatOrderDate = (d) => d ? new Date(d).toLocaleString("ro-RO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const orderLinks = (o) => o.item_type === "part"
  ? { admin: `/parts/${o.item_id}`, shop: `/?piesa=${encodeURIComponent(o.item_id)}` }
  : { admin: `/stock/${o.item_id}`, shop: `/?produs=${encodeURIComponent(o.item_id)}` };

const orderItemPrice = (o) => o.item_price_eur || o.item_current_price || 0;
const orderTotal = (o) => o.item_price_eur ? (o.total_eur || 0) : orderItemPrice(o) + (o.delivery_fee_eur || 0);

const STATUS_CONFIG = {
  new: { label: "Noua", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: Clock },
  confirmed: { label: "Confirmata", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", icon: CheckCircle2 },
  preparing: { label: "In pregatire", color: "bg-orange-500/20 text-orange-400 border-orange-500/30", icon: Package },
  out_for_delivery: { label: "In livrare", color: "bg-purple-500/20 text-purple-400 border-purple-500/30", icon: Truck },
  delivered: { label: "Livrata", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", icon: CheckCircle2 },
  cancelled: { label: "Anulata", color: "bg-red-500/20 text-red-400 border-red-500/30", icon: XCircle },
};

const REQUEST_STATUS = {
  new: { label: "Noua", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  replied: { label: "Raspuns", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
  fulfilled: { label: "Indeplinita", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
  cancelled: { label: "Anulata", color: "bg-red-500/20 text-red-400 border-red-500/30" },
};

function StatCard({ icon: Icon, label, value, subtext, color = "text-primary" }) {
  return (
    <Card className="border-border/50 bg-card/80 backdrop-blur-sm" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`p-3 rounded-lg bg-primary/10 ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-heading font-bold">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
          {subtext && <p className="text-xs text-muted-foreground mt-0.5">{subtext}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

const WHATSAPP_TEMPLATES = {
  confirmed: (order) => `Buna ziua ${order.first_name}! ✅ Comanda dvs. *${order.order_id}* a fost *confirmata*.\n\nProdus: ${order.item_name}\nTotal: ${order.total_eur} EUR\n${order.tracking_code ? `Cod tracking: ${order.tracking_code}\nUrmareste: ${window.location.origin}/track/${order.tracking_code}` : ""}\n\nVa multumim! - Scutersh`,
  preparing: (order) => `Buna ziua ${order.first_name}! 📦 Comanda dvs. *${order.order_id}* este acum *in pregatire*.\n\n${order.tracking_code ? `Urmareste comanda: ${window.location.origin}/track/${order.tracking_code}` : ""}\n\n- Scutersh`,
  out_for_delivery: (order) => `Buna ziua ${order.first_name}! 🚚 Comanda dvs. *${order.order_id}* este *in curs de livrare*!\n\nProdus: ${order.item_name}\nZona: ${order.zone || "N/A"}\n${order.tracking_code ? `Urmareste: ${window.location.origin}/track/${order.tracking_code}` : ""}\n\n- Scutersh`,
  delivered: (order) => `Buna ziua ${order.first_name}! 🎉 Comanda dvs. *${order.order_id}* a fost *livrata* cu succes!\n\nSper sa va bucurati de ${order.item_name}!\nPentru orice intrebare, suntem aici.\n\n- Scutersh`,
  cancelled: (order) => `Buna ziua ${order.first_name}. Comanda dvs. *${order.order_id}* a fost *anulata*.\n\nDaca aveti intrebari, nu ezitati sa ne contactati.\n\n- Scutersh`,
};

function openWhatsAppNotification(order, status) {
  const phone = (order.phone || "").replace(/[^0-9]/g, "");
  if (!phone) return;
  const intlPhone = phone.startsWith("0") ? "40" + phone.slice(1) : phone;
  const template = WHATSAPP_TEMPLATES[status];
  if (!template) return;
  const msg = encodeURIComponent(template(order));
  window.open(`https://wa.me/${intlPhone}?text=${msg}`, "_blank");
}

function OrderRow({ order, onStatusChange }) {
  const [expanded, setExpanded] = useState(false);
  const [updating, setUpdating] = useState(false);
  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.new;
  const StatusIcon = cfg.icon;

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/orders/${order.order_id}/status`, null, { params: { status: newStatus } });
      toast.success(`Comanda ${order.order_id} -> ${STATUS_CONFIG[newStatus]?.label}`);
      onStatusChange();
      // Auto-open WhatsApp notification
      if (order.phone && WHATSAPP_TEMPLATES[newStatus]) {
        openWhatsAppNotification({ ...order, status: newStatus }, newStatus);
      }
    } catch (e) {
      toast.error("Eroare la actualizare status");
    }
    setUpdating(false);
  };

  return (
    <div className="border border-border/50 rounded-lg overflow-hidden" data-testid={`order-${order.order_id}`}>
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-secondary/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <StatusIcon className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-bold">{order.order_id}</span>
              <Badge className={`text-[10px] px-1.5 py-0 border ${cfg.color}`}>{cfg.label}</Badge>
            </div>
            <p className="text-sm text-muted-foreground truncate">
              {order.first_name} {order.last_name} — {order.item_name || "N/A"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <span className="font-heading font-bold text-primary" data-testid={`order-total-${order.order_id}`}>{orderTotal(order).toFixed(0)} EUR</span>
          <span className="text-xs text-muted-foreground" data-testid={`order-date-${order.order_id}`}>
            {formatOrderDate(order.created_at)}
          </span>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </div>
      {expanded && (
        <div className="border-t border-border/50 p-4 bg-secondary/10 space-y-3 animate-fade-in">
          {/* Product details */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
            <p className="text-xs font-semibold text-primary mb-2">Produit commandé:</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm">{order.item_name || "N/A"}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                  <Badge variant="outline" className="text-[10px]">
                    {order.item_type === "part" ? "Pièce" : "Scooter"}
                  </Badge>
                  {order.item_id && (
                    <span className="font-mono">{order.item_id}</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Data comenzii: <strong className="text-foreground">{formatOrderDate(order.created_at)}</strong></p>
                {order.item_id && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    <a href={orderLinks(order).admin} target="_blank" rel="noopener noreferrer" data-testid={`order-admin-link-${order.order_id}`} onClick={e => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-primary/40 text-primary hover:bg-primary/10">
                      <ExternalLink className="w-3 h-3" /> {order.item_type === "part" ? "Deschide piesa" : "Deschide scuterul"} (gestiune)
                    </a>
                    <a href={orderLinks(order).shop} target="_blank" rel="noopener noreferrer" data-testid={`order-shop-link-${order.order_id}`} onClick={e => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-border text-muted-foreground hover:text-foreground">
                      <ExternalLink className="w-3 h-3" /> Vezi în magazin
                    </a>
                  </div>
                )}
              </div>
              <div className="text-right">
                <p className="text-[11px] text-muted-foreground">{order.item_type === "part" ? "Preț piesă" : "Preț scuter"}</p>
                <p className="font-bold text-primary text-lg" data-testid={`order-item-price-${order.order_id}`}>{orderItemPrice(order)} EUR</p>
                {!order.item_price_eur && order.item_current_price > 0 && (
                  <p className="text-[10px] text-yellow-500">preț actual (nesalvat în comandă)</p>
                )}
                {order.delivery_fee_eur > 0 && (
                  <p className="text-xs text-muted-foreground">+ {order.delivery_fee_eur} EUR livraison</p>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Phone className="w-3.5 h-3.5 text-muted-foreground" />
              <span>{order.phone}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-3.5 h-3.5 text-muted-foreground" />
              <span>{order.email || "—"}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
              <span>{order.delivery_address}, {order.city}</span>
            </div>
            <div className="flex items-center gap-2">
              <Truck className="w-3.5 h-3.5 text-muted-foreground" />
              <span>{order.zone || "N/A"} — {order.delivery_fee_eur} EUR livrare</span>
            </div>
          </div>
          {order.tracking_code && (
            <p className="text-xs font-mono bg-primary/10 text-primary px-2 py-1 rounded inline-block">
              Cod tracking: {order.tracking_code}
            </p>
          )}
          {order.notes && (
            <p className="text-sm italic text-muted-foreground">Note: {order.notes}</p>
          )}
          {/* WhatsApp notify button */}
          {order.phone && (
            <div className="pt-1">
              <Button
                size="sm"
                variant="outline"
                className="text-xs h-7 border-green-500/40 text-green-400 hover:bg-green-500/10"
                onClick={(e) => { e.stopPropagation(); openWhatsAppNotification(order, order.status); }}
                data-testid={`whatsapp-notify-${order.order_id}`}
              >
                <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Notifier WhatsApp
              </Button>
            </div>
          )}
          <div className="flex flex-wrap gap-2 pt-2">
            {Object.entries(STATUS_CONFIG).map(([key, val]) => (
              <Button
                key={key}
                size="sm"
                variant={order.status === key ? "default" : "outline"}
                disabled={order.status === key || updating}
                onClick={(e) => { e.stopPropagation(); handleStatusChange(key); }}
                className="text-xs h-7"
                data-testid={`order-status-${key}`}
              >
                {val.label}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PartRequestRow({ request, onStatusChange }) {
  const [updating, setUpdating] = useState(false);
  const cfg = REQUEST_STATUS[request.status] || REQUEST_STATUS.new;

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/part-requests/${request.request_id}/status`, null, { params: { status: newStatus } });
      toast.success("Status cerere actualizat");
      onStatusChange();
    } catch (e) {
      toast.error("Eroare la actualizare");
    }
    setUpdating(false);
  };

  return (
    <Card className="border-border/50" data-testid={`part-request-${request.request_id}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-bold">{request.request_id}</span>
            <Badge className={`text-[10px] px-1.5 py-0 border ${cfg.color}`}>{cfg.label}</Badge>
          </div>
          <span className="text-xs text-muted-foreground">
            {new Date(request.created_at).toLocaleDateString("ro-RO")}
          </span>
        </div>
        <p className="text-sm font-medium">{request.part_description}</p>
        <p className="text-xs text-muted-foreground">
          {request.scooter_brand} {request.scooter_model} {request.scooter_year && `(${request.scooter_year})`}
        </p>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span>{request.first_name} {request.last_name}</span>
          <span>{request.phone}</span>
          {request.email && <span>{request.email}</span>}
        </div>
        <div className="flex gap-2 pt-1">
          {Object.entries(REQUEST_STATUS).map(([key, val]) => (
            <Button
              key={key}
              size="sm"
              variant={request.status === key ? "default" : "outline"}
              disabled={request.status === key || updating}
              onClick={() => handleStatusChange(key)}
              className="text-xs h-7"
            >
              {val.label}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function DeliveryZoneRow({ zone, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState(zone.price_eur);
  const [days, setDays] = useState(zone.estimated_days || "");

  const handleSave = async () => {
    try {
      await axios.put(`${API}/delivery-zones/${zone.zone_id}`, null, {
        params: { price_eur: price, estimated_days: days }
      });
      toast.success(`Zone ${zone.zone_name} mise a jour`);
      setEditing(false);
      onUpdate();
    } catch (e) {
      toast.error("Eroare la salvare");
    }
  };

  return (
    <div className="flex items-center justify-between p-3 border border-border/50 rounded-lg" data-testid={`zone-${zone.zone_id}`}>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm">{zone.zone_name}</p>
        <p className="text-xs text-muted-foreground">{zone.estimated_days} zile</p>
      </div>
      {editing ? (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={price}
            onChange={(e) => setPrice(parseFloat(e.target.value))}
            className="w-20 h-8 text-sm"
          />
          <Input
            value={days}
            onChange={(e) => setDays(e.target.value)}
            className="w-20 h-8 text-sm"
            placeholder="zile"
          />
          <Button size="sm" className="h-8" onClick={handleSave}>OK</Button>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditing(false)}>X</Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="font-heading font-bold text-primary">{zone.price_eur} EUR</span>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing(true)}>
            Editeaza
          </Button>
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [partRequests, setPartRequests] = useState([]);
  const [zones, setZones] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderFilter, setOrderFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [exchangeRate, setExchangeRate] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ordersRes, statsRes, requestsRes, zonesRes, rateRes, reservRes] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/dashboard/sales`),
        axios.get(`${API}/part-requests`),
        axios.get(`${API}/public/delivery-zones`),
        axios.get(`${API}/public/exchange-rate`),
        axios.get(`${API}/reservations`),
      ]);
      setOrders(ordersRes.data);
      setStats(statsRes.data);
      setPartRequests(requestsRes.data);
      setZones(zonesRes.data);
      setExchangeRate(rateRes.data.eur_ron);
      setReservations(reservRes.data);
    } catch (e) {
      toast.error("Eroare la incarcarea datelor");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredOrders = orders.filter(o => {
    if (orderFilter !== "all" && o.status !== orderFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        (o.order_id || "").toLowerCase().includes(q) ||
        (o.first_name || "").toLowerCase().includes(q) ||
        (o.last_name || "").toLowerCase().includes(q) ||
        (o.tracking_code || "").toLowerCase().includes(q) ||
        (o.phone || "").includes(q)
      );
    }
    return true;
  });

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: TrendingUp },
    { id: "orders", label: "Comenzi", icon: ShoppingCart, count: orders.filter(o => o.status === "new").length },
    { id: "requests", label: "Cereri piese", icon: Wrench, count: partRequests.filter(r => r.status === "new").length },
    { id: "reservations", label: "Rezervari", icon: CalendarCheck, count: reservations.filter(r => r.status === "active").length },
    { id: "zones", label: "Zone livrare", icon: MapPin },
  ];

  if (!isAdmin || !isAdmin()) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-muted-foreground">Acces restrictionat. Doar administratorii pot accesa aceasta pagina.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold">Admin Panel</h1>
            <p className="text-sm text-muted-foreground">Gestioneaza comenzi, cereri si zone de livrare</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="btn-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Actualizeaza
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-secondary/30 p-1 rounded-lg overflow-x-auto" data-testid="admin-tabs">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                tab === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
              {t.count > 0 && (
                <span className="bg-destructive text-destructive-foreground text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* DASHBOARD TAB */}
        {tab === "dashboard" && stats && (
          <div className="space-y-6 animate-fade-in">
            {/* Admin Tools */}
            <Card className="border-orange-500/30 bg-orange-500/5">
              <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="font-bold text-sm">Curatare foto vechi</h3>
                  <p className="text-xs text-muted-foreground">Sterge URL-urile de poze care nu mai functioneaza (din vechiul server)</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                  data-testid="btn-clean-photos"
                  onClick={async () => {
                    try {
                      toast.info("Curatare in curs...");
                      const res = await axios.post(`${API}/admin/clean-broken-photos`);
                      const d = res.data;
                      toast.success(`Gata! ${d.parts_photos_removed} poze piese + ${d.scooters_photos_removed} poze scutere sterse`);
                    } catch (e) {
                      toast.error("Eroare la curatare");
                    }
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Sterge poze defecte
                </Button>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard icon={DollarSign} label="Chiffre d'affaires" value={`${stats.total_revenue_eur?.toFixed(0)} EUR`}
                subtext={exchangeRate ? `~${(stats.total_revenue_eur * exchangeRate).toFixed(0)} RON` : undefined} />
              <StatCard icon={ShoppingCart} label="Total comenzi" value={stats.total_orders} />
              <StatCard icon={Clock} label="In asteptare" value={stats.pending_orders} />
              <StatCard icon={Truck} label="In livrare" value={stats.in_transit} />
              <StatCard icon={CheckCircle2} label="Livrate" value={stats.delivered_orders} />
              <StatCard icon={XCircle} label="Anulate" value={stats.cancelled_orders} />
              <StatCard icon={Package} label="Scutere disponibile" value={stats.scooters_available} subtext={`din ${stats.total_scooters} total`} />
              <StatCard icon={AlertTriangle} label="Stoc scazut piese" value={stats.low_stock_parts?.length || 0} subtext={`${stats.out_of_stock_count} fara stoc`} />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Orders by Status Pie Chart */}
              <Card className="border-border/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Comenzi pe status</CardTitle>
                </CardHeader>
                <CardContent className="h-[220px]">
                  {(() => {
                    const COLORS = ["#3b82f6", "#eab308", "#f97316", "#a855f7", "#10b981", "#ef4444"];
                    const pieData = [
                      { name: "Noi", value: stats.pending_orders || 0 },
                      { name: "Confirmate", value: stats.confirmed_orders || 0 },
                      { name: "Pregatire", value: stats.preparing_orders || 0 },
                      { name: "Livrare", value: stats.in_transit || 0 },
                      { name: "Livrate", value: stats.delivered_orders || 0 },
                      { name: "Anulate", value: stats.cancelled_orders || 0 },
                    ].filter(d => d.value > 0);
                    return (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                            {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Revenue Bar Chart (from recent orders) */}
              <Card className="border-border/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Venituri recente (EUR)</CardTitle>
                </CardHeader>
                <CardContent className="h-[220px]">
                  {(() => {
                    const recentOrders = stats.recent_orders || [];
                    const byDay = {};
                    recentOrders.forEach(o => {
                      const day = o.created_at ? new Date(o.created_at).toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit" }) : "?";
                      byDay[day] = (byDay[day] || 0) + (o.total_eur || 0);
                    });
                    const barData = Object.entries(byDay).map(([day, total]) => ({ day, total: Math.round(total) })).slice(-10);
                    return (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={barData}>
                          <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#888" }} />
                          <YAxis tick={{ fontSize: 11, fill: "#888" }} />
                          <Tooltip />
                          <Bar dataKey="total" fill="hsl(82, 84%, 68%)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>

            {/* Recent Orders */}
            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Eye className="w-4 h-4" /> Comenzi recente
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/50 text-muted-foreground text-xs">
                        <th className="text-left p-3">ID</th>
                        <th className="text-left p-3">Data</th>
                        <th className="text-left p-3">Client</th>
                        <th className="text-left p-3 hidden sm:table-cell">Produs</th>
                        <th className="text-right p-3">Total</th>
                        <th className="text-center p-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recent_orders?.map(o => {
                        const cfg = STATUS_CONFIG[o.status] || STATUS_CONFIG.new;
                        return (
                          <tr key={o.order_id} className="border-b border-border/30 hover:bg-secondary/20">
                            <td className="p-3 font-mono text-xs">{o.order_id}</td>
                            <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{formatOrderDate(o.created_at)}</td>
                            <td className="p-3">{o.first_name} {o.last_name}</td>
                            <td className="p-3 hidden sm:table-cell truncate max-w-[200px]">{o.item_id ? <a href={orderLinks(o).admin} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{o.item_name}</a> : <span className="text-muted-foreground">{o.item_name}</span>}</td>
                            <td className="p-3 text-right font-bold">{o.total_eur?.toFixed(0)} EUR</td>
                            <td className="p-3 text-center">
                              <Badge className={`text-[10px] border ${cfg.color}`}>{cfg.label}</Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Low stock alerts */}
            {stats.low_stock_parts?.length > 0 && (
              <Card className="border-yellow-500/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 text-yellow-400">
                    <AlertTriangle className="w-4 h-4" /> Alerte stoc scazut
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {stats.low_stock_parts.map(p => (
                      <div key={p.part_id} className="flex justify-between items-center text-sm border-b border-border/30 pb-2">
                        <span>{p.name}</span>
                        <Badge variant="outline" className="text-yellow-400 border-yellow-500/30">
                          {p.quantity} / {p.min_stock} min
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pending part requests */}
            {partRequests.filter(r => r.status === "new").length > 0 && (
              <Card className="border-blue-500/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 text-blue-400">
                    <Wrench className="w-4 h-4" /> Cereri piese noi ({partRequests.filter(r => r.status === "new").length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {partRequests.filter(r => r.status === "new").slice(0, 5).map(r => (
                      <div key={r.request_id} className="text-sm border-b border-border/30 pb-2">
                        <p className="font-medium">{r.part_description}</p>
                        <p className="text-xs text-muted-foreground">
                          {r.first_name} {r.last_name} — {r.scooter_brand} {r.scooter_model}
                        </p>
                      </div>
                    ))}
                  </div>
                  {partRequests.filter(r => r.status === "new").length > 5 && (
                    <Button variant="link" size="sm" className="mt-2 p-0" onClick={() => setTab("requests")}>
                      Voir toutes les demandes
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ORDERS TAB */}
        {tab === "orders" && (
          <div className="space-y-4 animate-fade-in">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Cauta comanda (ID, nume, telefon, tracking)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-orders"
                />
              </div>
              <Select value={orderFilter} onValueChange={setOrderFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="filter-orders">
                  <SelectValue placeholder="Filtru status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-muted-foreground">{filteredOrders.length} comenzi</p>
            <div className="space-y-2">
              {filteredOrders.map(o => (
                <OrderRow key={o.order_id} order={o} onStatusChange={fetchAll} />
              ))}
              {filteredOrders.length === 0 && (
                <p className="text-center text-muted-foreground py-8">Nicio comanda gasita</p>
              )}
            </div>
          </div>
        )}

        {/* PART REQUESTS TAB */}
        {tab === "requests" && (
          <div className="space-y-4 animate-fade-in">
            <p className="text-sm text-muted-foreground">{partRequests.length} cereri de piese</p>
            <div className="grid gap-3">
              {partRequests.map(r => (
                <PartRequestRow key={r.request_id} request={r} onStatusChange={fetchAll} />
              ))}
              {partRequests.length === 0 && (
                <p className="text-center text-muted-foreground py-8">Nicio cerere de piese</p>
              )}
            </div>
          </div>
        )}

        {/* DELIVERY ZONES TAB */}
        {tab === "zones" && (
          <div className="space-y-4 animate-fade-in">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{zones.length} zone de livrare</p>
              {exchangeRate && (
                <p className="text-xs text-muted-foreground font-mono">1 EUR = {exchangeRate.toFixed(4)} RON (BNR)</p>
              )}
            </div>
            <div className="space-y-2">
              {zones.map(z => (
                <DeliveryZoneRow key={z.zone_id} zone={z} onUpdate={fetchAll} />
              ))}
            </div>
          </div>
        )}

        {/* RESERVATIONS TAB */}
        {tab === "reservations" && (
          <div className="space-y-4 animate-fade-in">
            <p className="text-sm text-muted-foreground">{reservations.length} rezervari</p>
            <div className="grid gap-3">
              {reservations.map(r => {
                const statusCfg = {
                  active: { label: "Activa", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
                  confirmed: { label: "Confirmata", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
                  cancelled: { label: "Anulata", color: "bg-red-500/20 text-red-400 border-red-500/30" },
                  expired: { label: "Expirata", color: "bg-gray-500/20 text-gray-400 border-gray-500/30" },
                };
                const cfg = statusCfg[r.status] || statusCfg.active;
                return (
                  <Card key={r.reservation_id} className="border-border/50" data-testid={`reservation-${r.reservation_id}`}>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CalendarCheck className="w-4 h-4 text-yellow-400" />
                          <span className="font-mono text-sm font-bold">{r.reservation_id}</span>
                          <Badge className={`text-[10px] px-1.5 py-0 border ${cfg.color}`}>{cfg.label}</Badge>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {r.created_at ? new Date(r.created_at).toLocaleDateString("ro-RO") : "—"}
                        </span>
                      </div>
                      <p className="text-sm font-medium">{r.scooter_name} — <span className="text-primary font-bold">{r.scooter_price} EUR</span></p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span>{r.first_name} {r.last_name}</span>
                        <span>{r.phone}</span>
                        {r.email && <span>{r.email}</span>}
                      </div>
                      {r.notes && <p className="text-xs italic text-muted-foreground">{r.notes}</p>}
                      <div className="flex gap-2 pt-1">
                        {Object.entries(statusCfg).map(([key, val]) => (
                          <Button
                            key={key}
                            size="sm"
                            variant={r.status === key ? "default" : "outline"}
                            disabled={r.status === key}
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/reservations/${r.reservation_id}/status`, null, { params: { status: key } });
                                toast.success(`Rezervare ${r.reservation_id} -> ${val.label}`);
                                fetchAll();
                              } catch { toast.error("Eroare"); }
                            }}
                            className="text-xs h-7"
                          >
                            {val.label}
                          </Button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {reservations.length === 0 && (
                <p className="text-center text-muted-foreground py-8">Nicio rezervare</p>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
